import React, { useEffect, useState } from 'react';
import Modal from '@/components/common/Modal';
import { Badge, Button, Icon } from '@/components/ui';
import { showToast } from '@/globalFunction';
import { AccountView } from './lib/mapAccount';
import { useActivateAccount } from './hooks/useActivateAccount';

interface AccountActionsModalProps {
  /** The account being managed, or null when the modal is closed. */
  account: AccountView | null;
  onClose: () => void;
}

type Step = 'menu' | 'confirm';

/**
 * Manage an account from the accounts list. Two steps in one modal rather than stacked
 * dialogs: 'menu' lists the actions available for the account, 'confirm' guards the
 * destructive-ish one. The action list is a list of one today; it exists so later actions
 * arrive without a redesign.
 *
 * Rendering is gated by the caller on account.canManage, which the BE computes.
 */
export const AccountActionsModal = ({
  account,
  onClose,
}: AccountActionsModalProps) => {
  const [step, setStep] = useState<Step>('menu');
  const [error, setError] = useState<string | null>(null);
  const activate = useActivateAccount();

  // Reopening for a different account must never inherit the previous one's step or error.
  useEffect(() => {
    if (account) {
      setStep('menu');
      setError(null);
    }
  }, [account?.id]);

  if (!account) return null;

  const onConfirm = async () => {
    setError(null);
    try {
      await activate.mutateAsync(account.id);
      showToast('success', 'Account has been activated');
      onClose();
    } catch {
      setError('Couldn’t activate this account. Please try again.');
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="Manage account" size="sm">
      <div className="tw-font-poppins">
        <div className="tw-flex tw-items-center tw-gap-[10px] tw-mb-[18px]">
          <span className="tw-text-[15px] tw-font-semibold tw-text-ds-ink">
            {account.name}
          </span>
          {!account.active && <Badge>Inactive</Badge>}
        </div>

        {step === 'menu' && (
          <button
            type="button"
            onClick={() => setStep('confirm')}
            className="tw-w-full tw-text-left tw-flex tw-items-center tw-gap-[12px] tw-p-[14px] tw-border tw-border-solid tw-border-ds-hairline tw-rounded-ds-m tw-bg-white tw-cursor-pointer hover:tw-bg-ds-sky-mist tw-transition-colors"
          >
            <Icon name="circle-play" size={20} color="var(--twilight-sky)" />
            <span className="tw-flex-1 tw-min-w-0">
              <span className="tw-block tw-text-[14px] tw-font-semibold tw-text-ds-ink">
                Activate account
              </span>
              <span className="tw-block tw-text-[12.5px] tw-text-ds-ink-mute tw-mt-[2px]">
                Restores access to this account’s workspace.
              </span>
            </span>
            <Icon name="chevron-right" size={16} color="var(--ink-mute)" />
          </button>
        )}

        {step === 'confirm' && (
          <div>
            <p className="tw-m-0 tw-text-[14px] tw-text-ds-ink-soft">
              Activate this account?
            </p>
            <p className="tw-mt-[6px] tw-mb-0 tw-text-[12.5px] tw-text-ds-ink-mute">
              Everyone assigned to it will be able to open its workspace again.
            </p>
            {error && (
              <p
                role="alert"
                className="tw-mt-[12px] tw-mb-0 tw-text-[13px] tw-text-ds-danger-text"
              >
                {error}
              </p>
            )}
            <div className="tw-flex tw-justify-end tw-gap-[10px] tw-mt-[20px]">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setStep('menu')}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={onConfirm}
                disabled={activate.isPending}
              >
                {activate.isPending ? 'Activating…' : 'Activate'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default AccountActionsModal;
